/**
 * Critical Error Context
 *
 * Manages global state for critical API failures that require full-screen error overlay.
 * Listens for error events from Axios interceptors and provides retry functionality.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { AxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';
import { ErrorType } from '../utils/errorClassification';
import { CriticalEndpointConfig } from '../config/criticalEndpoints';
import { getNetworkReconnectionManager } from '../utils/networkReconnectionManager';

/**
 * State for critical error overlay
 */
export interface CriticalErrorState {
  /** Whether the overlay is currently visible */
  isVisible: boolean;

  /** Type of error (server, network, auth) */
  errorType: ErrorType | null;

  /** Error message to display to user */
  message: string;

  /** Original request config for retry */
  requestConfig: AxiosRequestConfig | null;

  /** Endpoint configuration that failed */
  endpoint: CriticalEndpointConfig | null;

  /** Number of retry attempts */
  retryCount: number;

  /** Whether auto-retry is in progress */
  isAutoRetrying: boolean;

  /** Seconds until next retry (for countdown display) */
  nextRetryIn: number | null;

  /** Current auto-retry attempt number */
  autoRetryAttempt: number;
}

/**
 * Context value interface
 */
interface CriticalErrorContextType {
  /** Current error state */
  state: CriticalErrorState;

  /** Hide the error overlay */
  hideError: () => void;

  /** Retry the failed request */
  retry: () => Promise<void>;
}

/**
 * Default state for error overlay
 */
const defaultState: CriticalErrorState = {
  isVisible: false,
  errorType: null,
  message: '',
  requestConfig: null,
  endpoint: null,
  retryCount: 0,
  isAutoRetrying: false,
  nextRetryIn: null,
  autoRetryAttempt: 0
};

/**
 * Critical Error Context
 */
const CriticalErrorContext = createContext<CriticalErrorContextType | undefined>(undefined);

/**
 * Hook to use critical error context
 *
 * @throws Error if used outside of CriticalErrorProvider
 *
 * @example
 * ```typescript
 * const { state, retry, hideError } = useCriticalError();
 *
 * if (state.isVisible) {
 *   return <ErrorOverlay message={state.message} onRetry={retry} />;
 * }
 * ```
 */
export const useCriticalError = () => {
  const context = useContext(CriticalErrorContext);
  if (!context) {
    throw new Error('useCriticalError must be used within CriticalErrorProvider');
  }
  return context;
};

/**
 * Props for CriticalErrorProvider
 */
interface CriticalErrorProviderProps {
  children: React.ReactNode;
}

/**
 * Critical Error Provider
 *
 * Wraps the application and listens for critical error events from Axios interceptors.
 * Manages error overlay state and provides retry functionality.
 */
export const CriticalErrorProvider: React.FC<CriticalErrorProviderProps> = ({ children }) => {
  const [state, setState] = useState<CriticalErrorState>(defaultState);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const autoRetryRef = useRef<(() => Promise<void>) | null>(null);

  /**
   * Listen for critical error events from Axios interceptors
   */
  useEffect(() => {
    const handleShowError = (event: CustomEvent) => {
      const { type, message, requestConfig, endpoint } = event.detail;

      setState({
        isVisible: true,
        errorType: type,
        message,
        requestConfig,
        endpoint,
        retryCount: 0,
        isAutoRetrying: false,
        nextRetryIn: null,
        autoRetryAttempt: 0
      });

      // If this is a network error, start auto-reconnection attempts
      if (type === ErrorType.NETWORK_ERROR) {
        console.log('[CriticalError] Network error detected, starting auto-reconnection');
        const manager = getNetworkReconnectionManager();
        manager.startReconnectionAttempts();
      }
    };

    // Add event listener
    window.addEventListener('showCriticalError', handleShowError as EventListener);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('showCriticalError', handleShowError as EventListener);
    };
  }, []);

  /**
   * Listen for NetworkReconnectionManager events
   */
  useEffect(() => {
    const manager = getNetworkReconnectionManager();

    // Handle connection restored
    const handleConnectionRestored = () => {
      console.log('[CriticalError] Connection restored, triggering auto-retry');
      // Use ref to avoid stale closure
      if (autoRetryRef.current) {
        autoRetryRef.current();
      }
    };

    // Handle scheduled retry (countdown timer)
    const handleScheduleRetry = (data: any) => {
      console.log('[CriticalError] Retry scheduled:', data);
      setState(prev => ({
        ...prev,
        isAutoRetrying: true,
        nextRetryIn: data.nextRetryIn,
        autoRetryAttempt: data.attemptNumber
      }));

      // Start countdown interval
      startCountdown(data.nextRetryIn);
    };

    // Handle checking connection
    const handleCheckingConnection = (data: any) => {
      console.log('[CriticalError] Checking connection:', data);
      setState(prev => ({
        ...prev,
        isAutoRetrying: true,
        nextRetryIn: null, // Clear countdown while checking
        autoRetryAttempt: data.attemptNumber
      }));

      // Clear countdown interval
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };

    // Handle connection lost
    const handleConnectionLost = () => {
      console.log('[CriticalError] Connection lost');
      setState(prev => ({
        ...prev,
        isAutoRetrying: false,
        nextRetryIn: null,
        autoRetryAttempt: 0
      }));

      // Clear countdown interval
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };

    // Subscribe to events
    manager.on('connectionRestored', handleConnectionRestored);
    manager.on('scheduleRetry', handleScheduleRetry);
    manager.on('checkingConnection', handleCheckingConnection);
    manager.on('connectionLost', handleConnectionLost);

    // Cleanup on unmount
    return () => {
      manager.off('connectionRestored', handleConnectionRestored);
      manager.off('scheduleRetry', handleScheduleRetry);
      manager.off('checkingConnection', handleCheckingConnection);
      manager.off('connectionLost', handleConnectionLost);

      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, []); // Empty deps - we use setState with prev callback

  /**
   * Start countdown timer
   */
  const startCountdown = (seconds: number) => {
    // Clear existing interval
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    // Update countdown every second
    countdownIntervalRef.current = setInterval(() => {
      setState(prev => {
        if (prev.nextRetryIn === null || prev.nextRetryIn <= 0) {
          // Stop countdown
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          return prev;
        }

        return {
          ...prev,
          nextRetryIn: prev.nextRetryIn - 1
        };
      });
    }, 1000);
  };

  /**
   * Automatically retry the failed request (triggered by connection restoration)
   */
  const autoRetry = useCallback(async () => {
    setState(prevState => {
      if (!prevState.requestConfig) {
        console.log('[CriticalError] No request config for auto-retry');
        return prevState;
      }

      console.log('[CriticalError] Executing auto-retry');

      // Execute retry in async IIFE
      (async () => {
        try {
          // Update state to show we're retrying
          setState(prev => ({
            ...prev,
            isAutoRetrying: true,
            nextRetryIn: null
          }));

          // Dynamically import api to avoid circular dependencies
          const { api } = await import('../services/api');

          // Re-execute the failed request
          await api.request(prevState.requestConfig!);

          // Success! Stop reconnection attempts
          const manager = getNetworkReconnectionManager();
          manager.stopReconnectionAttempts();

          // Hide overlay
          setState(defaultState);

          // Stop reconnection attempts and clear countdown
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }

          // Show success toast
          toast.success('Conexão restaurada!', {
            duration: 3000
          });

          // For TIER 1 critical endpoints, refresh the page to reload all data
          if (prevState.endpoint?.tier === 1) {
            setTimeout(() => {
              window.location.reload();
            }, 500);
          }
        } catch (error) {
          console.error('[CriticalError] Auto-retry failed:', error);
          // Failed retry will be caught by interceptor and may trigger another error
          // Keep auto-retry active - manager will schedule next attempt
        }
      })();

      return prevState;
    });
  }, []);

  // Update autoRetry ref whenever it changes
  useEffect(() => {
    autoRetryRef.current = autoRetry;
  }, [autoRetry]);

  /**
   * Hide the error overlay and reset state
   */
  const hideError = useCallback(() => {
    // Stop auto-reconnection when overlay is hidden
    const manager = getNetworkReconnectionManager();
    manager.stopReconnectionAttempts();

    // Clear countdown interval
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    setState(defaultState);
  }, []);

  /**
   * Retry the failed request (manual retry triggered by user clicking button)
   *
   * Increments retry count, re-executes the failed request, and handles success/failure:
   * - On success: Hides overlay, shows success toast, and refreshes page for TIER 1 endpoints
   * - On failure: Error caught by interceptor, which re-shows overlay automatically
   */
  const retry = useCallback(async () => {
    if (!state.requestConfig) {
      return;
    }

    // Manual retry cancels auto-retry
    const manager = getNetworkReconnectionManager();
    manager.stopReconnectionAttempts();

    // Clear countdown
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    // Increment retry count
    setState(prev => ({
      ...prev,
      retryCount: prev.retryCount + 1,
      isAutoRetrying: false,
      nextRetryIn: null
    }));

    try {
      // Dynamically import api to avoid circular dependencies
      const { api } = await import('../services/api');

      // Re-execute the failed request
      await api.request(state.requestConfig);

      // Success! Hide overlay
      hideError();

      // Show success toast
      toast.success('Operação realizada com sucesso!');

      // For TIER 1 critical endpoints, refresh the page to reload all data
      // This ensures the app is in a consistent state after recovering from critical failure
      if (state.endpoint?.tier === 1) {
        // Small delay to show the success toast before refreshing
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      // Retry failed - error will be caught by interceptor
      // which will dispatch showCriticalError event again
      // This automatically re-shows the overlay with updated retry count
      console.error('Retry failed:', error);

      // If it's still a network error, restart auto-reconnection
      if (state.errorType === ErrorType.NETWORK_ERROR) {
        manager.startReconnectionAttempts();
      }
    }
  }, [state.requestConfig, state.endpoint, state.errorType, hideError]);

  /**
   * Context value
   */
  const value: CriticalErrorContextType = {
    state,
    hideError,
    retry
  };

  return (
    <CriticalErrorContext.Provider value={value}>
      {children}
    </CriticalErrorContext.Provider>
  );
};
